import type { FC, ReactNode } from 'react'

import { useSettings } from '@/components/providers/settings-provider'

interface DollShape {
    x: number
    y: number
    color: string
    ears: 'round' | 'tall' | 'point'
    scale?: number
}

const SHELF_DOLLS: Array<DollShape> = [
    { x: 250, y: 300, color: '#C8956A', ears: 'round' },
    { x: 360, y: 300, color: '#F4EBDD', ears: 'round' },
    { x: 470, y: 300, color: '#7A5640', ears: 'round' },
    { x: 250, y: 480, color: '#F3C9CF', ears: 'tall' },
    { x: 360, y: 480, color: '#A9BA9D', ears: 'tall' },
    { x: 470, y: 480, color: '#DCC5A8', ears: 'tall' },
    { x: 1130, y: 480, color: '#BDB5AE', ears: 'point' },
    { x: 1240, y: 480, color: '#F6D77B', ears: 'round', scale: 0.9 },
    { x: 1350, y: 480, color: '#9CB48A', ears: 'round', scale: 0.9 },
    { x: 1130, y: 300, color: '#F3C9CF', ears: 'tall', scale: 0.7 },
    { x: 1240, y: 300, color: '#C8956A', ears: 'round', scale: 0.7 },
    { x: 1350, y: 300, color: '#F6D77B', ears: 'round', scale: 0.7 },
    { x: 690, y: 470, color: '#F7F0E6', ears: 'point', scale: 0.95 },
    { x: 800, y: 470, color: '#A9BA9D', ears: 'point', scale: 0.95 },
    { x: 910, y: 470, color: '#E8A5B0', ears: 'round', scale: 0.95 }
]

const Doll: FC<DollShape> = ({ x, y, color, ears, scale = 1 }): ReactNode => {
    return (
        <g transform={`translate(${x} ${y}) scale(${scale})`}>
            <ellipse cx='0' cy='6' rx='38' ry='8' fill='#3a281e' opacity='0.12' />
            <ellipse cx='0' cy='-30' rx='34' ry='36' fill={color} />
            {ears === 'round' && (
                <>
                    <circle cx='-26' cy='-108' r='13' fill={color} />
                    <circle cx='26' cy='-108' r='13' fill={color} />
                </>
            )}
            {ears === 'tall' && (
                <>
                    <ellipse cx='-13' cy='-138' rx='9' ry='30' fill={color} />
                    <ellipse cx='13' cy='-138' rx='9' ry='30' fill={color} />
                </>
            )}
            {ears === 'point' && (
                <>
                    <path d='M-32 -100 L-22 -128 L-10 -106 Z' fill={color} />
                    <path d='M32 -100 L22 -128 L10 -106 Z' fill={color} />
                </>
            )}
            <circle cx='0' cy='-84' r='32' fill={color} />
            <circle cx='-11' cy='-86' r='3.6' fill='#2a1d16' />
            <circle cx='11' cy='-86' r='3.6' fill='#2a1d16' />
            <circle cx='-19' cy='-76' r='5' fill='#f2b3bd' opacity='0.8' />
            <circle cx='19' cy='-76' r='5' fill='#f2b3bd' opacity='0.8' />
            <path d='M-5 -74 Q0 -69 5 -74' stroke='#4a3328' strokeWidth='2' fill='none' strokeLinecap='round' />
        </g>
    )
}

const Shelf: FC<{ x: number, width: number, panel: string }> = ({ x, width, panel }): ReactNode => {
    return (
        <g>
            <rect x={x} y='120' width={width} height='560' rx='10' fill={panel} />
            <rect x={x - 12} y='110' width='16' height='580' rx='6' fill='#b88a5e' />
            <rect x={x + width - 4} y='110' width='16' height='580' rx='6' fill='#b88a5e' />
            {[120, 305, 485, 600].map(y => (
                <rect key={y} x={x - 12} y={y} width={width + 24} height='14' rx='5' fill='#c99a6b' />
            ))}
            <rect x={x} y='612' width={width} height='78' rx='6' fill='#f6ecdd' />
        </g>
    )
}

// Illustrated stand-in for the 3D boutique on devices without WebGL. The sign follows the store
// name from the admin settings, which server and browser both receive from the page data.
export const BoutiqueFallback: FC = (): ReactNode => {
    const { storeName } = useSettings()

    return (
        <svg
            viewBox='0 0 1600 900'
            preserveAspectRatio='xMidYMid slice'
            className='absolute inset-0 h-full w-full'
            role='img'
            aria-label={`Ilustrasi butik ${storeName} dengan boneka rajut di rak kayu`}
        >
            <defs>
                <pattern id='cw-wallpaper' width='80' height='80' patternUnits='userSpaceOnUse'>
                    <rect width='80' height='80' fill='#f8eee2' />
                    <circle cx='20' cy='20' r='5' fill='#f0cdd2' />
                    <circle cx='60' cy='60' r='5' fill='#f0cdd2' />
                    <circle cx='60' cy='20' r='2' fill='#c9d3bc' />
                    <circle cx='20' cy='60' r='2' fill='#c9d3bc' />
                </pattern>
                <radialGradient id='cw-glow' cx='50%' cy='30%' r='60%'>
                    <stop offset='0%' stopColor='#fff4e2' stopOpacity='0.9' />
                    <stop offset='100%' stopColor='#fff4e2' stopOpacity='0' />
                </radialGradient>
            </defs>

            <rect width='1600' height='900' fill='url(#cw-wallpaper)' />
            <rect y='600' width='1600' height='90' fill='#b8c7aa' />
            <rect y='690' width='1600' height='210' fill='#c9a07a' />
            {[0, 1, 2, 3, 4].map(row => (
                <rect key={row} y={700 + row * 42} width='1600' height='2' fill='#a77b55' opacity='0.5' />
            ))}

            <Shelf x={180} width={360} panel='#f2d3d8' />
            <Shelf x={1060} width={360} panel='#efe0cc' />

            <path d='M610 690 V320 A190 190 0 0 1 990 320 V690 Z' fill='#c9d6be' />
            <path d='M610 320 A190 190 0 0 1 990 320' fill='none' stroke='#f6ecdd' strokeWidth='14' />
            <rect x='610' y='485' width='380' height='14' rx='5' fill='#c99a6b' />
            <rect x='610' y='612' width='380' height='78' rx='6' fill='#f6ecdd' />
            <rect x='672' y='196' width='256' height='92' rx='22' fill='#b98a5e' />
            <text x='800' y='252' textAnchor='middle' fontFamily='Georgia, serif' fontStyle='italic' fontSize='40' textLength={storeName.length > 12 ? 220 : undefined} lengthAdjust='spacingAndGlyphs' fill='#fff7ec'>
                {storeName}
            </text>

            {SHELF_DOLLS.map(doll => (
                <Doll key={`${doll.x}-${doll.y}`} {...doll} />
            ))}

            <ellipse cx='800' cy='800' rx='260' ry='46' fill='#f3c9cf' />
            <ellipse cx='800' cy='800' rx='190' ry='32' fill='#fbf3e8' />
            <ellipse cx='800' cy='800' rx='120' ry='20' fill='#cbd8c0' />

            {[300, 1300].map(x => (
                <g key={x}>
                    <line x1={x} y1='0' x2={x} y2='70' stroke='#3e3129' strokeWidth='3' />
                    <path d={`M${x - 46} 112 Q${x} 50 ${x + 46} 112 Z`} fill='#ead9bf' />
                    <circle cx={x} cy='112' r='14' fill='#ffe4b8' />
                </g>
            ))}

            <rect width='1600' height='900' fill='url(#cw-glow)' />
        </svg>
    )
}
